import { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { Info, ShieldCheck } from "lucide-react";
import toast from "react-hot-toast";

import DistroList from "../../components/distros-list/distros-list.component";
import PageWrapper from "../../components/page-wrapper/page-wrapper.components";
import { setCurrentDistro } from "../../redux/distro/distro.actions";
import { selectCurrentDistro } from "../../redux/distro/distro.selector";
import { sessionExpired, updateToken } from "../../redux/user/user.actions";
import { selectCurrentUser } from "../../redux/user/user.selectors";
import { authFetch, getApiUrl, SessionExpiredError } from "../../utils/api";

const AccessTypesTooltip = () => {
    const [position, setPosition] = useState(null);

    const show = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const width = Math.min(320, window.innerWidth - 32);
        const left = Math.max(16, Math.min(rect.left + rect.width / 2 - width / 2, window.innerWidth - width - 16));
        setPosition({ top: rect.bottom + 8, left, width });
    };
    const hide = () => setPosition(null);

    return (
        <>
            <button
                type="button"
                aria-label="About access types"
                className="inline-flex align-middle ml-1 text-gray-400 hover:text-primary focus:text-primary focus:outline-none"
                onMouseEnter={show}
                onMouseLeave={hide}
                onFocus={show}
                onBlur={hide}
            >
                <Info className="w-3.5 h-3.5" />
            </button>
            {position && (
                <div
                    role="tooltip"
                    className="fixed z-50 px-3 py-2 text-xs font-normal normal-case tracking-normal text-white bg-gray-900 rounded-lg shadow-lg space-y-1.5 pointer-events-none"
                    style={position}
                >
                    <p><span className="font-semibold">Full Firm Access</span> users have access to the firm’s portal, financials, commission statements and clients.</p>
                    <p><span className="font-semibold">Client-Only Access</span> users have access to the firm’s portal and full client scope, but cannot view any financials or commission statements.</p>
                </div>
            )}
        </>
    );
};

const SecurityPage = ({ currentUser, currentDistro, setCurrentDistro, onSessionExpired, onTokenRefresh }) => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const toastIdRef = useRef(null);
    const apiUrl = getApiUrl();
    const authParams = { currentUser, onSessionExpired, onTokenRefresh };
    const firmName = currentUser?.distros?.find((d) => d.id === currentDistro)?.name;

    useEffect(() => {
        if (currentUser?.distros?.length === 1) {
            setCurrentDistro(currentUser.distros[0].id);
        }
    }, [currentUser?.id]);

    useEffect(() => {
        if (!currentUser?.token || currentDistro === null) return;

        const fetchUsers = async () => {
            setLoading(true);
            toastIdRef.current = toast.loading("Loading users...");
            try {
                const response = await authFetch(`${apiUrl}/distroaccount/${currentDistro}/users`, {}, authParams);
                if (response.ok) {
                    setUsers(await response.json());
                    toast.dismiss(toastIdRef.current);
                } else {
                    setUsers([]);
                    toast.error("Failed to load users", { id: toastIdRef.current });
                }
            } catch (err) {
                toast.dismiss(toastIdRef.current);
                if (!(err instanceof SessionExpiredError)) {
                    setUsers([]);
                    toast.error("Failed to load users");
                }
            } finally {
                setLoading(false);
            }
        };

        fetchUsers();
        return () => {
            if (toastIdRef.current) {
                toast.dismiss(toastIdRef.current);
            }
        };
    }, [currentUser?.id, currentDistro]);

    const handleClick = (e) => {
        setCurrentDistro(e.target.dataset.id);
    };

    return (
        <PageWrapper>
            {currentDistro === null ? (
                <DistroList distros={currentUser?.distros} handleClick={handleClick} />
            ) : (
                <div className="flex-1 px-6 md:px-8 py-6 pb-5">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-primary/10 rounded-xl">
                            <ShieldCheck className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Security</h1>
                            <p className="text-sm text-gray-500">
                                {firmName ? `People with access to ${firmName}` : "People with access to this firm"}
                            </p>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                                <thead className="bg-gray-50 border-b border-gray-100">
                                    <tr>
                                        <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">First Name</th>
                                        <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Last Name</th>
                                        <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Email</th>
                                        <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                            Access Type
                                            <AccessTypesTooltip />
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {users.map((user) => (
                                        <tr key={user.email}>
                                            <td className="px-5 py-3 text-gray-900">{user.firstName}</td>
                                            <td className="px-5 py-3 text-gray-900">{user.lastName}</td>
                                            <td className="px-5 py-3 text-gray-700">{user.email}</td>
                                            <td className="px-5 py-3">
                                                <span className="bg-sky-100 text-sky-800 border border-sky-300 rounded-full text-xs font-semibold px-2.5 py-0.5">
                                                    {user.accessTypeLabel}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {!loading && users.length === 0 && (
                            <p className="text-gray-500 text-sm py-6 text-center">No users found.</p>
                        )}
                    </div>
                    <p className="mt-4 text-sm text-gray-500">
                        Users with access to this firm’s portal. To add or remove a user, contact{" "}
                        <a href="mailto:support@nationalhealthclaim.com" className="text-primary hover:underline">
                            support@nationalhealthclaim.com
                        </a>
                    </p>
                </div>
            )}
        </PageWrapper>
    );
};

const mapStateToProps = createStructuredSelector({
    currentUser: selectCurrentUser,
    currentDistro: selectCurrentDistro,
});

const mapDispatchToProps = (dispatch) => ({
    setCurrentDistro: (data) => dispatch(setCurrentDistro(data)),
    onSessionExpired: () => dispatch(sessionExpired()),
    onTokenRefresh: (token) => dispatch(updateToken(token)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SecurityPage);
